import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  Snackbar,
  Alert,
} from "@mui/material";
import Carousel from "react-material-ui-carousel";

import crudsService from "services/cruds-service";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import Footer from "examples/Footer";
import CourseCard from "examples/Cards/CourseCard";
import MDButton from "components/MDButton";

import { AuthContext } from "context";
import MDTypography from "components/MDTypography";
import MDEditor from "components/MDEditor";

const DealInformation = () => {
  const classes = {
    media: {
      height: 400,
    },
    cardContent: {
      padding: "1 rem",
    },
    feedbackBox: {
      backgroundColor: "#f4f4f4",
      padding: "1 rem",
      borderRadius: "8px",
      marginBottom: "1 rem",
    },
  };
  const { id } = useParams(); // Get the deal ID from the URL params
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const { setIsAuthenticated, getCurrentUser } = useContext(AuthContext);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [user, setUser] = useState({});

  useEffect(() => {
    async function checkToken() {
      let user_id = await getCurrentUser();
      if (!user_id) {
        setIsAuthenticated(false);
        localStorage.removeItem("token");
      } else {
        setUser(user_id);
        return user_id;
      }
    }

    checkToken();
  }, []);

  useEffect(() => {
    async function getDealDetails(deal_id) {
      const response = await crudsService.getDeal(deal_id);
      setDeal(response.data);
      setLoading(false);
    }
    if (id !== undefined) getDealDetails(id);
  }, [id]);

  const purchaseDeal = async () => {
    try {
      const response = await crudsService.purchaseDeal({
        userId: user,
        dealId: deal._id,
      });
      setNotification({
        open: true,
        message: "Deal purchased successfully!",
        severity: "success",
      });
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (notification.open) {
      const timer = setTimeout(() => {
        setNotification({
          open: false,
          message: "",
          severity: "success",
        });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  if (loading) {
    return (
      <Container style={{ textAlign: "center", marginTop: "50px" }}>
        <CircularProgress />
        <Typography variant="h6" color="textSecondary">
          Loading deal details...
        </Typography>
      </Container>
    );
  }

  if (!deal) {
    return (
      <Container style={{ textAlign: "center", marginTop: "50px" }}>
        <Typography variant="h6" color="textSecondary">
          Deal not found.
        </Typography>
      </Container>
    );
  }

  return (
    <DashboardLayout>
      {notification.open && (
        <Snackbar open>
          <Alert variant="filled" sx={{ width: "100%" }} color="success">
            {notification.message}
          </Alert>
        </Snackbar>
      )}
      <DashboardNavbar />
      <Container>
        {/* Deal Image and Name */}
        <Grid container spacing={4} style={{ marginTop: "40px" }}>
          <Grid item xs={12} md={6}>
            <Card>
              <img
                className={classes.media}
                src={deal.deal_image_url || "https://via.placeholder.com/400"}
                alt={deal.deal_name}
              />
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <CardContent className={classes.cardContent}>
              <MDTypography variant="h4" >
                {deal.deal_name}
              </MDTypography>
              <MDTypography variant="h6"  color="success">
                ${deal.price}
              </MDTypography>
              <div data-color-mode="light">
                <MDEditor.Markdown
                  source={deal.deal_description}
                  style={{ color: "#344767", background: "transparent" }}
                  components={{
                    p: ({ node, ...props }) => (
                      <p style={{ color: "#7b809a" }} {...props} />
                    ),
                  }}
                />
              </div>

              <Box mt={2}>
                <MDTypography variant="subtitle1">
                  Duration: {deal.duration}
                </MDTypography>
                <MDTypography variant="subtitle1">
                  Level: {deal.level}
                </MDTypography>
                <MDTypography variant="subtitle1">
                  Delivery Method: {deal.delivery_method}
                </MDTypography>
                <MDButton
                  color="info"
                  variant="gradient"
                  onClick={purchaseDeal}
                >
                  Register
                </MDButton>
              </Box>
            </CardContent>
          </Grid>
        </Grid>

        {/* List of Courses Included */}
        {deal.courses_included && deal.courses_included.length > 0 && (
          <Box mt={4}>
            <MDTypography variant="h5" >
              Courses Included
            </MDTypography>
            <Grid container spacing={3}>
              {deal.courses_included.map((course, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <CourseCard
                    key={index}
                    image={course.app_image_location}
                    title={course.course_name}
                    description={course.short_description.substring(0, 80)}
                    moduleCount={course.course_names_videos.length}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Participant Feedback */}
        {deal.participant_feedback && deal.participant_feedback.length > 0 && (
          <Box className={classes.feedbackBox} mt={4}>
            <MDTypography variant="h5" >
              Participant Feedback
            </MDTypography>
            {/* carousel of participant feedback */}
            <Carousel
              sx={{
                marginTop: "10px",
                paddingTop: "40px",
              }}
              animation="slide"
              navButtonsAlwaysVisible={true}
              interval={5000}
              duration={1750}
              changeOnFirstRender={true}
            >
              {deal.participant_feedback.map((feedback, index) => (
                <Card
                  key={index}
                  style={{
                    height: "150px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    paddingLeft: "5rem",
                    paddingRight: "5rem",
                  }}
                >
                  <CardContent>
                    <MDTypography variant="h6" >
                      {feedback.participant}
                    </MDTypography>
                    <MDTypography variant="body2" color="textSecondary">
                      {feedback.feedback}
                    </MDTypography>
                  </CardContent>
                </Card>
              ))}
            </Carousel>
          </Box>
        )}
      </Container>
      <MDBox mt={4} mb={4} />
    </DashboardLayout>
  );
};

export default DealInformation;
